import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface BaseProps {
  children: ReactNode
  /** Flaticon suffix rendered as a leading `<i>`, e.g. `fast-forward-double-right-arrows-symbol`. */
  icon?: string
  className?: string
}

type BtnProps =
  | (BaseProps & { to: string; href?: never; onClick?: never; type?: never })
  | (BaseProps & { href: string; to?: never; onClick?: never; type?: never })
  /**
   * The button form, and **the only one that takes `disabled`.**
   *
   * A link cannot be disabled — there is no such attribute on `<a>`, and the usual
   * imitations (`pointer-events: none`, `aria-disabled`) leave it in the tab order and
   * followable by keyboard. So the union puts `disabled` only here rather than on
   * `BaseProps`, and `<Btn to="…" disabled>` is a compile error instead of a link that
   * looks unavailable and is not.
   *
   * Added 2026-09-13, when this button started submitting things that take time.
   */
  | (BaseProps & {
      to?: never
      href?: never
      onClick?: () => void
      type?: 'button' | 'submit'
      disabled?: boolean
    })

/**
 * The `.ul-btn` pill button. It appears as both a link and a form control, so the
 * element is chosen by which prop you pass:
 *
 *   <Btn to="/contact" icon="…">Join With us</Btn>   → <Link class="ul-btn">
 *   <Btn href="#" icon="…">All Testimonials</Btn>    → <a class="ul-btn">
 *   <Btn type="submit" icon="…">Get in Touch</Btn>   → <button class="ul-btn">
 *
 */
export function Btn(props: BtnProps) {
  const { children, icon, className } = props
  const classes = className ? `ul-btn ${className}` : 'ul-btn'
  // Only emit the separating space when there is actually an icon before it —
  // `{false} {children}` would otherwise leave a stray leading text node.
  const inner = icon ? (
    <>
      <i className={`flaticon-${icon}`} /> {children}
    </>
  ) : (
    children
  )

  if ('to' in props && props.to !== undefined) {
    return (
      <Link to={props.to} className={classes}>
        {inner}
      </Link>
    )
  }
  if ('href' in props && props.href !== undefined) {
    return (
      <a href={props.href} className={classes}>
        {inner}
      </a>
    )
  }
  return (
    <button
      type={props.type ?? 'button'}
      className={classes}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      {inner}
    </button>
  )
}
